import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { RoomCapacityImpact } from '../utils/capacity-impact';

interface Props {
  readonly open: boolean;
  readonly impacts: RoomCapacityImpact[];
  /**
   * Everything else in the selection — labels, desk shapes, desks that were never bookable.
   * None of it touches a room's capacity, but all of it is about to be deleted, and a dialog
   * that only mentions desks would be quietly understating what the button does.
   */
  readonly otherCount: number;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Confirms deleting desks that are bookable, spelling out what each room loses.
 *
 * Shown only when capacity would actually change. Deleting a label, a desk shape, or a desk
 * that was never bookable costs a room nothing, and a dialog that appears every time teaches
 * an admin to dismiss it without reading — which is exactly the habit that makes the one
 * that matters slip through.
 */
const DeleteDesksDialog = ({ open, impacts, otherCount, onConfirm, onCancel }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const totalDesks = impacts.reduce((total, impact) => total + impact.deskCount, 0);

  // Only rooms this deletion empties. One already at zero has nothing left to warn about, and
  // saying it does reads as though the deletion caused it.
  const emptiedRooms = impacts.filter((impact) => impact.capacityAfter === 0 && impact.capacityBefore > 0);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{t('floorplan-editor-delete-desks-title', { count: totalDesks })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText variant="body2">
            {t('floorplan-editor-delete-desks-explanation')}
          </DialogContentText>

          <Stack spacing={0.5}>
            {impacts.map((impact) => (
              <Typography key={impact.roomId} variant="body2">
                <strong>{impact.roomName}</strong>
                {': '}
                {t('floorplan-editor-delete-desks-room-impact', {
                  desks: impact.deskCount,
                  before: impact.capacityBefore,
                  after: impact.capacityAfter,
                })}
              </Typography>
            ))}
          </Stack>

          {otherCount > 0 && (
            <Typography variant="body2">
              {t('floorplan-editor-delete-desks-other-elements', { count: otherCount })}
            </Typography>
          )}

          {emptiedRooms.length > 0 && (
            <Alert severity="warning">
              {t('floorplan-editor-delete-desks-room-emptied', {
                rooms: emptiedRooms.map((impact) => impact.roomName).join(', '),
              })}
            </Alert>
          )}

          <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
            {t('floorplan-editor-delete-desks-applied-on-save')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button onClick={onConfirm} color="error" variant="contained" autoFocus>
          {t('delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDesksDialog;
